// dataSlice.js
import { v4 as uuidv4 } from 'uuid';

export const createDataSlice = (set, get) => ({
  // Load survey with questions and settings from external source
  loadSurveyData: (surveyData) =>
    set((state) => {
      try {
        const { questions = [], survey: surveySettings = {}, settings = {} } = surveyData;
        
        // Base survey structure with all properties
        const baseSurvey = {
          id: null,
          title: 'Untitled Survey',
          description: '',
          category: '',
          createdAt: null,
          updatedAt: null,
          status: 'DRAFT',
          templateId: null,
          startDate: null,
          endDate: null,
          targetResponse: null,
          maxResponse: null,
          allowAnonymous: false,
          redirectUrl: null,
          messageAfterComplete: null,
          redirectTimeAfterMessage: 0,
          customCss: null,
          additionalSettings: null,
          pageWidth: '900px', //added new
          pagePaddingDesktop: '2rem',
          pagePaddingTablet: '1.5rem',
          pagePaddingMobile: '0.5rem',
          formContainerShowCard: false,
          formContainerCardShadow: false,
          formContainerPadding: '2rem',
          formContainerBackgroundColor: 'auto', // auto uses surface color
          formContainerBorderRadius: 'auto', // auto uses theme border radius
          headerPosition:"center",
          headingType:"h1",
          descriptionPosition: "center",
          descriptionColor:"",
          icon:"journal-bookmark",
          iconColor:"info" //primary,warning,success,danger,info
        };
        
        // Merge base survey structure with provided data and settings
        const mergedSurvey = {
          ...baseSurvey,
          ...surveySettings, // Survey metadata
          ...settings, // Additional dynamic settings
          updatedAt: new Date().toISOString(),
        };

        // Validate and process questions
        const validatedQuestions = Array.isArray(questions) 
          ? questions
              .map((q) => ({
                  ...q,
                  internalId: q.internalId || uuidv4(), // Use existing internalId or generate new one
                  id: q.id || null, // Keep server ID if exists
                }))
              .sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0)) // Sort by sequenceNumber
          : [];

        return {
          survey: mergedSurvey,
          questions: validatedQuestions,
          selectedQuestionId: null,
          isDirty: false,
          error: null,
        };
      } catch (error) {
        return {
          ...state,
          error: `Failed to load survey data: ${error.message}`,
        };
      }
    }),

  // JSON operations
  exportToJSON: () => {
    const state = get();
    return {
      survey: state.survey,
      questions: state.questions,
      metadata: {
        exportedAt: new Date().toISOString(),
        version: '1.0',
      },
    };
  },

  loadFromJSON: (jsonData) =>
    set((state) => {
      try {
        const { survey: surveyData, questions, settings } = jsonData;
        
        // Validate the JSON structure
        if (!Array.isArray(questions)) {
          throw new Error('Invalid JSON structure: questions must be an array');
        }

        // Base survey structure with all properties
        const baseSurvey = {
          id: null,
          title: 'Untitled Survey',
          description: '',
          category: '',
          createdAt: null,
          updatedAt: null,
          status: 'DRAFT',
          templateId: null,
          startDate: null,
          endDate: null,
          targetResponse: null,
          maxResponse: null,
          allowAnonymous: false,
          redirectUrl: null,
          messageAfterComplete: null,
          redirectTimeAfterMessage: 0,
          customCss: null,
          additionalSettings: null,
          pageWidth: '900px', //added new
          pagePaddingDesktop: '2rem',
          pagePaddingTablet: '1.5rem',
          pagePaddingMobile: '0.5rem',
          formContainerShowCard: false,
          formContainerCardShadow: false,
          formContainerPadding: '2rem',
          formContainerBackgroundColor: 'auto', // auto uses surface color
          formContainerBorderRadius: 'auto', // auto uses theme border radius
          headerPosition:"center",
          headingType:"h1",
          descriptionPosition: "center",
          descriptionColor:"",
          icon:"journal-bookmark",
          iconColor:"info" //primary,warning,success,danger,info
        };

        // Merge base survey structure with provided data and settings
        const mergedSurvey = {
          ...baseSurvey,
          ...surveyData, // Survey metadata from JSON
          ...settings, // Additional dynamic settings from JSON
          updatedAt: new Date().toISOString(),
        };

        // Ensure all questions have required fields
        const validatedQuestions = questions
          .map((q) => ({
            ...q,
            internalId: q.internalId || uuidv4(), // Use existing internalId or generate new one
            id: q.id || null, // Keep server ID if exists
          }))
          .sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0)); // Sort by sequenceNumber

        return {
          survey: mergedSurvey,
          questions: validatedQuestions,
          selectedQuestionId: null,
          isDirty: false,
          error: null,
        };
      } catch (error) {
        return {
          ...state,
          error: `Failed to load JSON: ${error.message}`,
        };
      }
    }),

  // Save/Load operations
  saveSurvey: async () => {
    set({ isLoading: true, error: null });
    
    try {
      const state = get();
      const surveyData = {
        survey: { ...state.survey, updatedAt: new Date().toISOString() },
        questions: state.questions,
      };

      // Here you would make an API call to save the survey
      // const response = await axios.post('/api/surveys', surveyData);
      
      // For now, just simulate a successful save
      await new Promise((resolve) => setTimeout(resolve, 1000));

      set({
        survey: surveyData.survey,
        isDirty: false,
        isLoading: false,
      });

      return { success: true };
    } catch (error) {
      set({
        error: `Failed to save survey: ${error.message}`,
        isLoading: false,
      });
      return { success: false, error: error.message };
    }
  },

  loadSurvey: async (surveyId) => {
    set({ isLoading: true, error: null });
    
    try {
      // Here you would make an API call to load the survey
      // const response = await axios.get(`/api/surveys/${surveyId}`);
      
      // For now, just simulate loading
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Mock data - replace with actual API response
      const mockData = {
        survey: {
          id: surveyId,
          title: 'Sample Survey',
          description: 'This is a sample survey',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          status: 'DRAFT',
        },
        questions: [],
      };

      set({
        survey: mockData.survey,
        questions: mockData.questions,
        selectedQuestionId: null,
        isDirty: false,
        isLoading: false,
      });

      return { success: true };
    } catch (error) {
      set({
        error: `Failed to load survey: ${error.message}`,
        isLoading: false,
      });
      return { success: false, error: error.message };
    }
  },
});